import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/browser';
import { AxiosError } from 'axios';
import { BaseEmployeeDTO, EmployeeDTOStatusEnum, TenantRoleDTO, UserDTO, UserDTOMarketingConsentEnum } from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { isWafBlockedError, WafBlockedError } from '../error/api-errors';
import { reportError } from '../error/sentry';
import { AdminTenantAccessData, flattenSwitchableTenants, getAdminTenantAccessData } from './permissionSelectors';

export enum MeInitializationResult {
  SUCCESS = 'SUCCESS',
  // the /me API returns 403 forbidden if no user is found in the DB for the given access token
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  MAINTENANCE = 'MAINTENANCE',
  // the user exists but doesn't have any active or inactive employment, i.e. cannot be assigned to any tenant
  NO_VALID_EMPLOYMENT = 'NO_VALID_EMPLOYMENT',
  // the account still needs to be unlocked by the employer after registration
  REGISTRATION_PENDING = 'REGISTRATION_PENDING',
  ACCOUNT_MERGED = 'ACCOUNT_MERGED',
  NEEDS_TO_COMPLETE_PROFILE = 'NEEDS_TO_COMPLETE_PROFILE',
  API_ERROR = 'API_ERROR',
  WAF_BLOCKED = 'WAF_BLOCKED',
}

export interface MeState {
  user?: UserDTO;
  employment?: BaseEmployeeDTO;
  currentTenantId?: string;
  adminTenantAccessData?: AdminTenantAccessData;
  tenantAdminRoles: Pick<TenantRoleDTO, 'tenantId' | 'tenantName'>[];
  initializationResult?: MeInitializationResult;
  marketingConsent: UserDTOMarketingConsentEnum;
  showWelcomeMessage?: boolean;
  wafCauseId?: string;
  accountMergeMail?: string;
  /** Kept across /me refetches until the access token contains the new login email. */
  loginMailOverride?: string;
  /**
   * The most recent loadMe request, i.e. the only one whose response may still be applied. Every other response is
   * stale, either because a newer request overtook it or because the access token subject changed while it was in
   * flight. The native app keeps its JS context across a logout, so applying a stale response would undo `resetMe`
   * and put the previous user, employment and tenant back into the state.
   */
  activeLoadRequestId?: string;
}

const initialState: MeState = {
  user: undefined,
  tenantAdminRoles: [],
  marketingConsent: UserDTOMarketingConsentEnum.NotSeen,
};
interface MaintenanceModeResponse {
  reason: 'MAINTENANCE_MODE';
  message: string;
  cause?: never;
}

interface WafBlockedResponse {
  message: string;
  cause: string;
  reason?: never;
}

export const loadMe = createAsyncThunk('me/loadMe', async () => {
  try {
    const user = (await apis.me.getMe()).data;
    let adminTenantAccessData: AdminTenantAccessData = {
      switchableTenants: [],
      canSwitch: false,
    };
    try {
      adminTenantAccessData = await getAdminTenantAccessData(user);
    } catch {
      // best-effort enrichment — don't block login if admin data is unavailable
    }
    return {
      user,
      adminTenantAccessData,
    };
  } catch (err) {
    const axiosErr = err as AxiosError<MaintenanceModeResponse | WafBlockedResponse>;
    const status = axiosErr?.response?.status;
    const reason = axiosErr.response?.data?.reason;
    if (isWafBlockedError(axiosErr)) {
      const wafData = axiosErr.response?.data as WafBlockedResponse;
      const cause = wafData?.cause ?? 'unknown';
      reportError(new WafBlockedError(cause), 'external', {
        tags: { wafCause: cause },
        fingerprint: ['waf-blocked', cause],
      });
      return {
        initializationResult: MeInitializationResult.WAF_BLOCKED,
        wafCauseId: wafData?.cause,
      };
    }
    if (status === 403) {
      return {
        initializationResult: MeInitializationResult.USER_NOT_FOUND,
      };
    }
    if (status === 503 && reason === 'MAINTENANCE_MODE') {
      return {
        initializationResult: MeInitializationResult.MAINTENANCE,
      };
    }
    throw err;
  }
});

const LOCAL_STORAGE_KEY = 'selectedTenantId';

const IS_APP = !!import.meta.env.VITE_WEB_MANAGER_URL;

function applyLoginMailToMatchingEmployments(
  employments: BaseEmployeeDTO[] | undefined,
  previousMail: string | undefined,
  nextMail: string,
): void {
  employments?.forEach(employment => {
    if (employment.mail === previousMail) {
      employment.mail = nextMail;
    }
  });
}

export const meSlice = createSlice({
  name: 'me',
  initialState,
  reducers: {
    /**
     * Drops everything that belongs to the currently logged in user. The native app keeps its
     * JS context across a logout, so without this the next user would start with the previous
     * user's IDs (`currentTenantId`, `employment`) and a stale `initializationResult`.
     *
     * Resetting to the initial state clears `activeLoadRequestId` as well, which invalidates the loadMe requests
     * still in flight - their responses would otherwise restore the very user dropped here.
     */
    resetMe: () => {
      // stop attributing errors to the user that just logged out
      Sentry.setUser(null);
      return initialState;
    },
    switchTenant: (state, action: PayloadAction<{ tenantId: string; isAdmin?: boolean }>) => {
      if (IS_APP) {
        // Normal user: switch to different active employment
        const newEmployment = state.user?.employments?.find(employment => employment.tenantId === action.payload.tenantId);
        if (newEmployment) {
          state.employment = newEmployment;
          state.currentTenantId = newEmployment.tenantId;
          localStorage.setItem(LOCAL_STORAGE_KEY, newEmployment.tenantId);
        }
      } else {
        state.employment = state.user?.employments?.find(employment => employment.tenantId === action.payload.tenantId);
        state.currentTenantId = action.payload.tenantId;
        const switchableTenants = flattenSwitchableTenants(state.adminTenantAccessData?.switchableTenants ?? []);
        if (switchableTenants.some(switchableTenant => switchableTenant.id === action.payload.tenantId)) {
          // save the tenant if the user actually belongs to it (i.e. not superadmin)
          localStorage.setItem(LOCAL_STORAGE_KEY, action.payload.tenantId);
        }
      }
    },
    setMarketingConsent: (state, action: PayloadAction<UserDTOMarketingConsentEnum>) => {
      state.marketingConsent = action.payload;
    },
    setShowWelcomeMessage: (state, action: PayloadAction<boolean>) => {
      state.showWelcomeMessage = action.payload;
    },
    setLoginMail: (state, action: PayloadAction<string>) => {
      state.loginMailOverride = action.payload;
      if (!state.user) {
        return;
      }
      const previousLoginMail = state.user.mail;
      state.user.mail = action.payload;
      applyLoginMailToMatchingEmployments(state.user.employments, previousLoginMail, action.payload);
      if (state.employment && state.employment.mail === previousLoginMail) {
        state.employment.mail = action.payload;
      }
      Sentry.setUser({ id: state.user.id, email: action.payload });
    },
  },
  extraReducers: builder => {
    builder.addCase(loadMe.pending, (state, action) => {
      // this request takes over the state; any older one still in flight is stale from here on
      state.activeLoadRequestId = action.meta.requestId;
    });
    builder.addCase(loadMe.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.activeLoadRequestId) {
        return;
      }
      state.activeLoadRequestId = undefined;

      if (action.payload.initializationResult) {
        state.initializationResult = action.payload.initializationResult;
        state.wafCauseId = action.payload.wafCauseId;
        return;
      }

      // load last selected tenant from local storage
      const selectedTenantId = localStorage.getItem(LOCAL_STORAGE_KEY);
      const employments = action.payload.user?.employments
        ?.filter(employee => employee.status === EmployeeDTOStatusEnum.Active || employee.status === EmployeeDTOStatusEnum.Inactive)
        .sort(
          (a, b) =>
            // order: last selected first, then active before inactive
            (b.tenantId === selectedTenantId ? 1 : 0) - (a.tenantId === selectedTenantId ? 1 : 0) ||
            (b.status === EmployeeDTOStatusEnum.Active ? 1 : 0) - (a.status === EmployeeDTOStatusEnum.Active ? 1 : 0),
        );
      // The forest already lists the tenants the user works at before the ones only reachable below them, so
      // flattening it and pulling the last selected one to the front is the whole ordering.
      const switchableTenants = flattenSwitchableTenants(action.payload.adminTenantAccessData.switchableTenants).sort(
        (a, b) => (b.id === selectedTenantId ? 1 : 0) - (a.id === selectedTenantId ? 1 : 0),
      );

      let currentTenantId: string | undefined;
      if (IS_APP) {
        currentTenantId = employments?.[0]?.tenantId;
      } else {
        currentTenantId = switchableTenants[0]?.id;
      }

      const accountMergeMail = action.payload.user?.accountMerge?.contactMail;
      if (!currentTenantId && accountMergeMail) {
        state.initializationResult = MeInitializationResult.ACCOUNT_MERGED;
        state.accountMergeMail = accountMergeMail;
        return;
      }

      if (
        IS_APP &&
        !currentTenantId &&
        action.payload.user?.employments?.some(employee => employee.status === EmployeeDTOStatusEnum.Registered)
      ) {
        state.initializationResult = MeInitializationResult.REGISTRATION_PENDING;
        return;
      } else if (!currentTenantId) {
        state.initializationResult = MeInitializationResult.NO_VALID_EMPLOYMENT;
        return;
      }

      if (employments?.some(employee => employee.requiredActions?.includes('UpdateProfile'))) {
        state.initializationResult = MeInitializationResult.NEEDS_TO_COMPLETE_PROFILE;
        return;
      }

      state.initializationResult = MeInitializationResult.SUCCESS;
      const fetchedLoginMail = action.payload.user?.mail;
      if (state.loginMailOverride && fetchedLoginMail === state.loginMailOverride) {
        state.loginMailOverride = undefined;
      }
      state.user = action.payload.user;
      if (state.loginMailOverride && state.user) {
        state.user.mail = state.loginMailOverride;
        applyLoginMailToMatchingEmployments(state.user.employments, fetchedLoginMail, state.loginMailOverride);
      }
      state.marketingConsent = action.payload.user?.marketingConsent || UserDTOMarketingConsentEnum.NotSeen;
      state.showWelcomeMessage = action.payload.user?.showWelcomeMessage;
      state.employment = employments?.find(employee => employee.tenantId === currentTenantId);
      if (state.loginMailOverride && state.employment && state.employment.mail === fetchedLoginMail) {
        state.employment.mail = state.loginMailOverride;
      }
      state.currentTenantId = currentTenantId;
      state.adminTenantAccessData = action.payload.adminTenantAccessData;
      // Only the user id is sent to Sentry - email is PII we don't need for triage (PRO-6603).
      Sentry.setUser({ id: action.payload.user?.id });
    });
    builder.addCase(loadMe.rejected, (state, action) => {
      if (action.meta.requestId !== state.activeLoadRequestId) {
        // a stale request must not show the current session an error page either
        return;
      }
      state.activeLoadRequestId = undefined;
      state.initializationResult = MeInitializationResult.API_ERROR;
    });
  },
});

// Action creators are generated for each case reducer function
export const { resetMe, switchTenant, setShowWelcomeMessage, setLoginMail } = meSlice.actions;

export const meReducer = meSlice.reducer;

export function isTenantSelected(): boolean {
  return !!localStorage.getItem(LOCAL_STORAGE_KEY);
}
